"use client";

import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  type Format,
  formatMeasure,
  formatTableValue,
} from "@/components/tools/geometry/format";
import {
  SolidFigure,
  SolidInputPanel,
  SolidMetricsTable,
  useSolidInputs,
} from "@/components/tools/geometry/solid-inputs";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type ExactFormat,
  formatExactWithUnit,
  isRationalExact,
} from "@/lib/tool-kit/geometry/exact";
import { answerUnit } from "@/lib/tool-kit/geometry/measurements";
import { evaluateSolid } from "@/lib/tool-kit/geometry/solid-input";
import type { SolidId } from "@/lib/tool-kit/geometry/solids";
import { conversionRows, unitSymbol } from "@/lib/tool-kit/geometry/units";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";
import { needsFraction } from "@/lib/tool-kit/math/rational";

/** English strings. A translated content file overrides these through `ui`. */
export const VOLUME_CALCULATOR_STRINGS = {
  solidLabel: "Solid",
  solids: {
    cube: "Cube",
    box: "Rectangular prism (box)",
    triPrism: "Triangular prism",
    cylinder: "Cylinder",
    cone: "Cone",
    sphere: "Sphere",
    hemisphere: "Hemisphere",
    pyramid: "Pyramid (rectangular base)",
    frustum: "Cone frustum (truncated cone)",
    ellipsoid: "Ellipsoid",
  },
  fields: {
    side: "Edge length (a)",
    length: "Length (l)",
    width: "Width (w)",
    height: "Height (h)",
    radius: "Radius (r)",
    sideA: "Triangle side a",
    sideB: "Triangle side b",
    sideC: "Triangle side c",
    prismLength: "Prism length (L)",
    bottomRadius: "Bottom radius (R)",
    topRadius: "Top radius (r)",
    semiA: "Semi-axis a",
    semiB: "Semi-axis b",
    semiC: "Semi-axis c",
  },
  unitLabel: "Unit of length",
  mixLabel: "Measurements are in different units",
  resultUnitLabel: "Give the answer in",
  decimalsLabel: "Decimal places",
  unitAria: "{field}: unit",
  lengthUnits: {
    mm: "Millimeters (mm)",
    cm: "Centimeters (cm)",
    m: "Meters (m)",
    km: "Kilometers (km)",
    in: "Inches (in)",
    ft: "Feet (ft)",
    yd: "Yards (yd)",
    mi: "Miles (mi)",
  },
  results: "Results",
  volume: "Volume",
  volumeDecimal: "Volume as a decimal",
  moreHeading: "Other measurements",
  measurementHeader: "Measurement",
  exactHeader: "Exact",
  decimalHeader: "Decimal",
  metrics: {
    baseArea: "Base area",
    topArea: "Top area",
    slantHeight: "Slant height",
    slantOnLength: "Slant height of the faces on the length sides",
    slantOnWidth: "Slant height of the faces on the width sides",
    spaceDiagonal: "Space diagonal",
    faceDiagonal: "Face diagonal",
    crossSection: "Great-circle cross-section area",
  },
  workingHeading: "The working",
  figureHeading: "Figure",
  figureTitle: "{solid} with its measurements labeled",
  downloadFigure: "Download figure (PNG)",
  slantLabel: "l = {value}",
  unitsHeading: "The same volume in other units",
  unitHeader: "Unit",
  volumeHeader: "Volume",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "{solid}: volume = {exact} ≈ {decimal}",
  summaryExact: "{solid}: volume = {exact}",
  enter: "Enter every measurement to see the volume.",
  invalid:
    "Enter each measurement as a number — decimals and fractions such as 3/4 work.",
  positive: "Every measurement must be greater than zero.",
  triangle:
    "These three sides cannot make a triangle: the longest side must be shorter than the other two added together.",
} as const;

type Strings = typeof VOLUME_CALCULATOR_STRINGS;

const ID_PREFIX = "vol";

function needsDecimal(
  volume: Parameters<typeof isRationalExact>[0],
  format: ExactFormat
): boolean {
  if (!isRationalExact(volume)) {
    return true;
  }
  return volume.some((term) =>
    format.decimals ? needsFraction(term.coef) : term.coef.den !== BigInt(1)
  );
}

function Results({
  solid,
  outcome,
  strings,
  unit,
  places,
  fmt,
}: {
  solid: SolidId;
  outcome: Extract<ReturnType<typeof evaluateSolid>, { ok: true }>;
  strings: Strings;
  unit: Parameters<typeof unitSymbol>[0];
  places: number;
  fmt: Format;
}) {
  const { result } = outcome;
  const exactFormat: ExactFormat = {
    decimals: outcome.format.style === "decimal",
    mark: outcome.format.mark,
  };
  const volumeUnit = unitSymbol(unit, 3);
  const exact = formatExactWithUnit(result.volume, volumeUnit, exactFormat);
  const decimal = `${formatMeasure(fmt, result.volumeValue, places)} ${volumeUnit}`;
  const showDecimal = needsDecimal(result.volume, exactFormat);
  const summary = t(showDecimal ? strings.summary : strings.summaryExact, {
    solid: strings.solids[solid],
    exact,
    decimal,
  });
  const rows = conversionRows(result.volumeValue, unit, 3).map((row) => [
    row.symbol,
    formatTableValue(fmt, row.value),
  ]);
  const slant = result.metrics.find((m) => m.key === "slantHeight");
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.volume} value={exact} />
        {showDecimal ? (
          <StatCard label={strings.volumeDecimal} value={decimal} />
        ) : null}
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={summary}
      />
      {result.metrics.length > 0 ? (
        <>
          <h3 className={WIDGET_H3}>{strings.moreHeading}</h3>
          <SolidMetricsTable
            fmt={fmt}
            format={exactFormat}
            headers={{
              measurement: strings.measurementHeader,
              exact: strings.exactHeader,
              decimal: strings.decimalHeader,
            }}
            labels={strings.metrics}
            metrics={result.metrics}
            places={places}
            unit={unit}
          />
        </>
      ) : null}
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
        <p>{result.volumeFormula}</p>
        {result.volumeSteps.map((step) => (
          <p key={step}>{step}</p>
        ))}
      </div>
      <h3 className={WIDGET_H3}>{strings.figureHeading}</h3>
      <SolidFigure
        fileName={`volume-${solid}`}
        outcome={outcome}
        slant={
          slant
            ? t(strings.slantLabel, {
                value: `${formatMeasure(fmt, slant.value, places)} ${unitSymbol(unit, 1)}`,
              })
            : undefined
        }
        solid={solid}
        strings={strings}
      />
      <h3 className={WIDGET_H3}>{strings.unitsHeading}</h3>
      <DataTable
        head={[strings.unitHeader, strings.volumeHeader]}
        rows={rows}
      />
    </>
  );
}

function errorMessage(strings: Strings, error: string): string {
  const messages: Record<string, string> = {
    invalid: strings.invalid,
    positive: strings.positive,
    triangle: strings.triangle,
  };
  return messages[error] ?? strings.invalid;
}

export default function VolumeCalculatorWidget() {
  const strings = useToolUi(VOLUME_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const mark = decimalMarkOf(fmt);
  const state = useSolidInputs("cylinder");
  const outcome = evaluateSolid(state.solid, state.values, state.setup, mark);
  let body: React.ReactNode;
  if (outcome.ok) {
    body = (
      <Results
        fmt={fmt}
        outcome={outcome}
        places={Number(state.places)}
        solid={state.solid}
        strings={strings}
        unit={answerUnit(state.setup)}
      />
    );
  } else if (outcome.error === "enter") {
    body = <Notice>{strings.enter}</Notice>;
  } else {
    body = <ErrorNotice>{errorMessage(strings, outcome.error)}</ErrorNotice>;
  }
  return (
    <>
      <SolidInputPanel idPrefix={ID_PREFIX} state={state} strings={strings} />
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}
